import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/UI/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave } = FiIcons;

const MessageSettings = () => {
  const navigate = useNavigate();
  const { t, language, customMessages, updateCustomMessages } = useLanguage();
  
  const [messages, setMessages] = useState({
    TOP_DISTINGUISHED: '',
    PASSED: '',
    FAIL: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize form with default translations or custom messages if available
    setMessages({
      TOP_DISTINGUISHED: customMessages.TOP_DISTINGUISHED || t('statusMessages.TOP_DISTINGUISHED'),
      PASSED: customMessages.PASSED || t('statusMessages.PASSED'),
      FAIL: customMessages.FAIL || t('statusMessages.FAIL')
    });
  }, [customMessages, t]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Validate messages are not empty
      if (!messages.TOP_DISTINGUISHED.trim() || 
          !messages.PASSED.trim() || 
          !messages.FAIL.trim()) {
        throw new Error("Messages cannot be empty");
      }
      
      // Save the custom messages
      updateCustomMessages(messages);
      
      // Show success message briefly
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setMessages(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetToDefaults = () => {
    setMessages({
      TOP_DISTINGUISHED: t('statusMessages.TOP_DISTINGUISHED'),
      PASSED: t('statusMessages.PASSED'),
      FAIL: t('statusMessages.FAIL')
    });
  };

  return (
    <Layout title={t('messageSettings')}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Button
                onClick={() => navigate('/admin')}
                variant="secondary"
                size="sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>{t('back')}</span>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('messageSettings')}
              </h1>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Top Distinguished Message */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('topDistinguishedMessage')}
              </label>
              <textarea
                value={messages.TOP_DISTINGUISHED}
                onChange={(e) => handleChange('TOP_DISTINGUISHED', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-minecraft-green focus:border-minecraft-green"
                rows="4"
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              ></textarea>
            </div>

            {/* Passed Message */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('passedMessage')}
              </label>
              <textarea
                value={messages.PASSED}
                onChange={(e) => handleChange('PASSED', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-minecraft-green focus:border-minecraft-green"
                rows="4"
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              ></textarea>
            </div>

            {/* Failed Message */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('failedMessage')}
              </label>
              <textarea
                value={messages.FAIL}
                onChange={(e) => handleChange('FAIL', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-minecraft-green focus:border-minecraft-green"
                rows="4"
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              ></textarea>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
                {t('messageUpdated')}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 rtl:space-x-reverse pt-6 border-t border-gray-200">
              <Button
                type="button"
                onClick={resetToDefaults}
                variant="secondary"
              >
                Reset to Defaults
              </Button>
              
              <Button
                type="button"
                onClick={() => navigate('/admin')}
                variant="secondary"
              >
                {t('cancel')}
              </Button>
              
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <span>{t('loading')}</span>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{t('save')}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
};

export default MessageSettings;